import { useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/store';
import { fetchProfile } from '../store/slices/profileSlice';
import { fetchConversations } from '../store/slices/chatSlice';
import { fetchReferralData } from '../store/slices/referralSlice';
import { logger } from '../utils/logger';

/**
 * Global data cache manager hook
 * Prefetches and caches commonly accessed data to improve performance
 */
export function useDataCache() {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);

  /**
   * Prefetch all common data on app load
   * Uses smart caching - only fetches if cache is stale
   */
  const prefetchCommonData = useCallback(() => {
    if (!isAuthenticated) return;

    logger.log('🔄 Prefetching common data for smooth navigation...');
    
    // Fetch data in parallel - each thunk has its own cache validation
    Promise.allSettled([
      dispatch(fetchProfile(false)),
      dispatch(fetchConversations(false)),
      dispatch(fetchReferralData(false)),
    ]).then(() => {
      logger.log('✅ Data prefetch complete');
    });
  }, [dispatch, isAuthenticated]);

  /**
   * Invalidate all caches and force refresh
   */
  const refreshAllData = useCallback(() => {
    if (!isAuthenticated) return;

    logger.log('🔄 Force refreshing all data...');
    
    Promise.allSettled([
      dispatch(fetchProfile(true)),
      dispatch(fetchConversations(true)),
      dispatch(fetchReferralData(true)),
    ]).then(() => {
      logger.log('✅ Data refresh complete');
    });
  }, [dispatch, isAuthenticated]);

  /**
   * Get cache status for all slices
   */
  const getCacheStatus = useCallback(() => {
    return {
      referral: 'Check referral slice for cache status',
      profile: 'Check profile slice for cache status',
      chat: 'Check chat slice for cache status',
    };
  }, []);

  return {
    prefetchCommonData,
    refreshAllData,
    getCacheStatus,
  };
}

/**
 * Hook to automatically prefetch data on mount
 */
export function useAutoPrefetch() {
  const { prefetchCommonData } = useDataCache();
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);

  useEffect(() => {
    if (isAuthenticated) {
      // Small delay to let auth complete
      const timer = setTimeout(() => {
        prefetchCommonData();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, prefetchCommonData]);
}